import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  TrendingUp, Users, DollarSign, UserMinus, Loader2,
  Bookmark, Archive, BookOpen, Crown, Send,
} from 'lucide-react';
import AppShell from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { gradeAccent } from '@/lib/brand';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASON_LABELS = {
  'too-expensive': 'Too expensive',
  'not-using': 'Not using enough',
  'missing-features': 'Missing features',
  'found-alternative': 'Found alternative',
  'other': 'Other',
};

/**
 * Admin metrics dashboard — single-shot data load from /api/admin/analytics/overview
 * + /api/admin/analytics/cancellations. Surfaces MRR, growth, churn, top pinned units,
 * recent transactions, and cancellation reasons in one Editorial Archive layout.
 */
export default function AdminMetricsPage() {
  const [data, setData] = useState(null);
  const [cancellations, setCancellations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingDigest, setSendingDigest] = useState(false);

  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      await axios.post(`${API}/admin/digest/send-now`);
      toast.success('Weekly digest sent to your inbox');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send digest');
    } finally {
      setSendingDigest(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [overview, cancels] = await Promise.all([
          axios.get(`${API}/admin/analytics/overview`),
          axios.get(`${API}/admin/analytics/cancellations`).catch(() => ({ data: { items: [], counts_by_reason: {} } })),
        ]);
        setData(overview.data);
        setCancellations(cancels.data);
      } catch (err) {
        toast.error('Failed to load analytics');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <AppShell>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#14213D]" />
        </div>
      </AppShell>
    );
  }

  if (!data) {
    return (
      <AppShell>
        <div className="min-h-[60vh] flex items-center justify-center text-sm text-[#5A6577]">
          Analytics unavailable.
        </div>
      </AppShell>
    );
  }

  const { subscribers, revenue, growth, top_pinned_units, recent_transactions, content } = data;

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-10" data-testid="admin-metrics-page">
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-2">Admin &middot; Insights</p>
            <h1 className="font-serif-display text-4xl text-[#14213D] tracking-tight">Metrics Dashboard</h1>
            <p className="text-sm text-[#5A6577] mt-1">
              As of {new Date(data.as_of).toLocaleString()} &middot; updates each visit
            </p>
          </div>
          <Button
            onClick={sendDigestNow}
            disabled={sendingDigest}
            className="bg-[#14213D] hover:bg-[#0D1730] text-[#FDFBF7] shrink-0"
            data-testid="send-digest-now-btn"
          >
            {sendingDigest
              ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending&hellip;</>
              : <><Send className="h-4 w-4 mr-2" />Send Digest Now</>}
          </Button>
        </div>

        {/* Top KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <Kpi
            icon={DollarSign}
            label="Monthly Recurring Revenue"
            value={`$${revenue.mrr.toFixed(2)}`}
            sub={`${revenue.arr.toFixed(0)} ARR annualized`}
            accent="#D4AF37"
            testid="kpi-mrr"
          />
          <Kpi
            icon={Crown}
            label="Paid Subscribers"
            value={subscribers.paid_total}
            sub={`${subscribers.basic} basic · ${subscribers.pro} pro`}
            accent="#14213D"
            testid="kpi-paid"
          />
          <Kpi
            icon={Users}
            label="Total Users"
            value={subscribers.total}
            sub={`+${growth.new_users_30d} in last 30 days`}
            accent="#1D5BAC"
            testid="kpi-users"
          />
          <Kpi
            icon={UserMinus}
            label="30-Day Churn"
            value={`${growth.churn_rate_30d_pct}%`}
            sub={`${growth.cancellations_30d} cancellation${growth.cancellations_30d === 1 ? '' : 's'}`}
            accent="#9B2C2C"
            testid="kpi-churn"
          />
        </div>

        {/* Two-column main grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
          {/* Growth + content */}
          <Card className="p-6 border-[#EBE7E0] bg-white">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="h-4 w-4 text-[#D4AF37]" />
              <h2 className="font-serif-display text-lg text-[#14213D]">Growth</h2>
            </div>
            <Stat label="New users (7d)" value={growth.new_users_7d} />
            <Stat label="New users (30d)" value={growth.new_users_30d} />
            <Stat label="New subscriptions (30d)" value={growth.new_subscriptions_30d} />
            <Stat label="Cancellations (30d)" value={growth.cancellations_30d} />
          </Card>

          <Card className="p-6 border-[#EBE7E0] bg-white">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="h-4 w-4 text-[#D4AF37]" />
              <h2 className="font-serif-display text-lg text-[#14213D]">Library</h2>
            </div>
            <Stat label="Curriculum units" value={content.total_units} />
            <Stat label="Primary sources" value={content.total_primary_sources} />
            <Stat label="Admin accounts" value={subscribers.admin} />
            <Stat label="Free accounts" value={subscribers.free} />
          </Card>

          {/* Cancellation reasons */}
          <Card className="p-6 border-[#EBE7E0] bg-white">
            <div className="flex items-center gap-2 mb-4">
              <UserMinus className="h-4 w-4 text-[#9B2C2C]" />
              <h2 className="font-serif-display text-lg text-[#14213D]">Cancellation Reasons</h2>
            </div>
            {cancellations && Object.keys(cancellations.counts_by_reason).length > 0 ? (
              Object.entries(cancellations.counts_by_reason).map(([reason, count]) => (
                <Stat key={reason} label={REASON_LABELS[reason] || reason} value={count} />
              ))
            ) : (
              <p className="text-xs text-[#A0AEC0] italic">No cancellation feedback yet.</p>
            )}
          </Card>
        </div>

        {/* Top pinned units */}
        <Card className="p-6 border-[#EBE7E0] bg-white mb-10">
          <div className="flex items-center gap-2 mb-4">
            <Bookmark className="h-4 w-4 text-[#D4AF37]" />
            <h2 className="font-serif-display text-lg text-[#14213D]">Most-Pinned Units</h2>
          </div>
          {top_pinned_units.length === 0 ? (
            <p className="text-xs text-[#A0AEC0] italic">No teacher has pinned a unit yet.</p>
          ) : (
            <div className="space-y-2">
              {top_pinned_units.map((u, i) => {
                const accent = gradeAccent(u.grade_level);
                return (
                  <div key={u.unit_id} className="flex items-center gap-4 py-2 border-b border-[#EBE7E0] last:border-0" data-testid={`top-pinned-${i}`}>
                    <span className="font-serif-display text-2xl text-[#D4AF37] w-8">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] uppercase tracking-[0.15em] font-semibold" style={{ color: accent.hex }}>
                        Grade {u.grade_level} &middot; Unit {u.unit_number} &middot; {accent.subject}
                      </p>
                      <p className="font-serif-display text-sm text-[#14213D] truncate">{u.title}</p>
                    </div>
                    <span className="text-sm font-medium text-[#14213D] whitespace-nowrap">
                      {u.pin_count} pin{u.pin_count === 1 ? '' : 's'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Recent transactions */}
        <Card className="p-6 border-[#EBE7E0] bg-white">
          <div className="flex items-center gap-2 mb-4">
            <Archive className="h-4 w-4 text-[#D4AF37]" />
            <h2 className="font-serif-display text-lg text-[#14213D]">Recent Paid Transactions</h2>
          </div>
          {recent_transactions.length === 0 ? (
            <p className="text-xs text-[#A0AEC0] italic">No paid transactions yet. Set up your Stripe webhook to start tracking.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[11px] uppercase tracking-[0.15em] text-[#5A6577] font-semibold border-b border-[#EBE7E0]">
                  <tr>
                    <th className="text-left py-2 pr-4">Date</th>
                    <th className="text-left py-2 pr-4">Customer</th>
                    <th className="text-left py-2 pr-4">Tier</th>
                    <th className="text-left py-2 pr-4">Interval</th>
                    <th className="text-right py-2">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {recent_transactions.map((t, i) => (
                    <tr key={t.created_at + i} className="border-b border-[#EBE7E0] last:border-0">
                      <td className="py-2 pr-4 text-[#5A6577] whitespace-nowrap">
                        {new Date(t.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-2 pr-4 text-[#14213D] truncate max-w-xs">{t.user_email}</td>
                      <td className="py-2 pr-4 text-[#14213D] capitalize">{t.tier}</td>
                      <td className="py-2 pr-4 text-[#5A6577] capitalize">{t.interval}</td>
                      <td className="py-2 text-right font-medium text-[#14213D]">${t.amount?.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </AppShell>
  );
}

function Kpi({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <Card className="p-5 border-[#EBE7E0] bg-white relative overflow-hidden" data-testid={testid}>
      <div className="absolute top-0 left-0 w-1 h-full" style={{ background: accent }} />
      <div className="flex items-center gap-2 mb-2 pl-2">
        <Icon className="h-4 w-4" style={{ color: accent }} />
        <p className="text-[11px] uppercase tracking-[0.18em] text-[#5A6577] font-semibold">{label}</p>
      </div>
      <p className="font-serif-display text-3xl text-[#14213D] pl-2 leading-none">{value}</p>
      {sub && (
        <p className="text-xs text-[#5A6577] mt-2 pl-2">{sub}</p>
      )}
    </Card>
  );
}

function Stat({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-[#EBE7E0] last:border-0">
      <span className="text-sm text-[#2D3748]">{label}</span>
      <span className="font-serif-display text-base text-[#14213D] font-medium">{value}</span>
    </div>
  );
}
